// ═══════════════════════════════════════════════════════════════════════════
// Las actividades económicas de una CSF, y si alguna es hacer vino
//
// El operador: "Para que una vinicola se pueda registrar su CSF debe incluir
// la producción de vino, etc." Y después, preguntado si tenía que ser la
// actividad principal: "Pasa aún cuando la actividad no sea la principal."
//
// ── DE DÓNDE SALE EL FORMATO ───────────────────────────────────────────────
//
// De las CSF reales que este proyecto guarda, no de suponer. El SAT las lista
// así, en texto plano, con encabezado y una línea por actividad:
//
//     Actividades Económicas:
//     Orden Actividad Económica Porcentaje Fecha Inicio Fecha Fin
//     3 Restaurantes-bar con servicio de meseros 50 25/10/2017
//     1 Asalariado 29 05/10/2017
//     1 Alquiler de Otros inmuebles 15 01/02/2013
//
// Ninguna de las dos CSF de muestra hace vino, así que el caso POSITIVO no se
// pudo comprobar contra un documento real y las pruebas lo cubren con líneas
// construidas con el mismo formato. Queda dicho: el día que llegue la CSF de
// una vinícola de verdad, conviene pasarla por aquí antes de fiarse.
//
// ── POR QUÉ NO SE BUSCA UN CÓDIGO ──────────────────────────────────────────
//
// La tentación es buscar la clave SCIAN, que sería exacta. No está: la CSF
// imprime la DESCRIPCIÓN de la actividad, no su clave. Así que se busca por
// texto, con todo lo que eso implica — y por eso esto avisa en vez de decidir.
//
// ── TRES RESPUESTAS, NO DOS ────────────────────────────────────────────────
//
//   produccion   hace vino (o uva, o bebidas fermentadas): elaborar, fabricar,
//                producir, cultivar
//   relacionada  vive del vino pero no lo hace: comercio, distribución, venta
//   otra         nada que ver
//
// La del medio existe porque «Comercio al por mayor de vinos y licores»
// contiene la palabra vino y NO es producción. Meterla en el mismo saco que
// «Elaboración de vinos» dejaría entrar a cualquier distribuidor; meterla en
// «otra» escondería que la CSF sí habla de vino y que quizá falta mirar mejor.
// Se dice lo que es y decide quien administra.
// ═══════════════════════════════════════════════════════════════════════════

/** `1 Alquiler de Otros inmuebles 15 01/02/2013` */
const RE_ACTIVIDAD =
  /^(\d+)\s+(.+?)\s+(\d{1,3})\s+(\d{2}\/\d{2}\/\d{4})(?:\s+(\d{2}\/\d{2}\/\d{4}))?$/;

/**
 * Palabras que YA significan hacer vino, ellas solas.
 *
 * Existen porque el esquema de dos ejes —un verbo de hacer MÁS una palabra de
 * vino— no las cubre: «Viticultura» no contiene «vino» ni «uva», y salía
 * clasificada como «otra». Lo encontró la prueba, no la lectura del código.
 * Meterlas a la fuerza en la lista de VINO habría hecho que «Comercio de
 * productos vitivinícolas» pasara por producción.
 */
const ES_HACER_VINO = [
  "viticultura", "vitivinicola", "vitivinícola", "vinicola", "vinícola",
  "vinificacion", "vinificación",
];

/** Hacer. El verbo es lo que separa producir de revender. */
const HACER = [
  "elaboracion", "elaboración", "fabricacion", "fabricación",
  "produccion", "producción", "cultivo", "destilacion", "destilación",
];

/** Vino, o de lo que se hace el vino. */
const VINO = [
  "vino", "vinos", "uva", "uvas", "vid", "vitivin",
  "bebidas fermentadas", "bebidas alcoholicas", "bebidas alcohólicas",
  "sidra", "mosto",
];

/** Vivir del vino sin hacerlo. */
const REVENDER = [
  "comercio", "venta", "distribucion", "distribución", "mayoreo", "menudeo",
  "abarrotes", "restaurante", "bar ", "supermercado", "tienda",
];

const sinAcentos = (s) => String(s || "").toLowerCase()
  .replace(/[áà]/g, "a").replace(/[éè]/g, "e").replace(/[íì]/g, "i")
  .replace(/[óò]/g, "o").replace(/[úù]/g, "u").trim();

const contiene = (texto, agujas) => agujas.some(a => texto.includes(sinAcentos(a)));

/**
 * Extrae las actividades económicas de las líneas de una CSF.
 *
 * Devuelve [] cuando no hay sección o no se pudo leer: NO es lo mismo que «no
 * hace vino», y quien llame tiene que distinguirlo — por eso la clasificación
 * de abajo devuelve `sin_actividades` y no `no`.
 */
export function extraerActividades(lineas) {
  const salida = [];
  let dentro = false;

  for (const cruda of lineas || []) {
    const linea = String(cruda || "").trim();
    if (!linea) continue;

    if (/^actividades\s+econ/i.test(linea)) { dentro = true; continue; }
    if (!dentro) continue;

    // El encabezado de la tabla, que no es una actividad.
    if (/^orden\s+actividad/i.test(linea)) continue;
    // La siguiente sección: se acabaron.
    if (/^(reg[ií]menes|obligaciones)\s*:?\s*$/i.test(linea)) break;
    // Los pies de página del SAT se cuelan entre secciones en CSF de varias
    // páginas; no cortan la tabla, solo no son actividades.
    if (/^p[áa]gina\s*\[/i.test(linea)) continue;

    const m = RE_ACTIVIDAD.exec(linea);
    if (!m) continue;
    salida.push({
      orden: Number(m[1]),
      descripcion: m[2].trim(),
      porcentaje: Number(m[3]),
      desde: m[4],
      hasta: m[5] || null,
    });
  }
  return salida;
}

/** Qué es una actividad: `produccion`, `relacionada` u `otra`. */
export function clasificarActividad(descripcion) {
  const t = sinAcentos(descripcion);
  if (!t) return "otra";

  // Primero lo que se revende, porque «Comercio de productos vitivinícolas»
  // lleva una palabra de la lista de abajo y sigue siendo comercio.
  const revende = contiene(t, REVENDER);
  if (contiene(t, ES_HACER_VINO)) return revende ? "relacionada" : "produccion";

  const hablaDeVino = contiene(t, VINO);
  if (!hablaDeVino) return "otra";
  // El orden importa: «Comercio al por mayor de vinos» tiene vino Y comercio.
  // Se mira primero si REVENDE, porque el verbo de hacer puede aparecer de
  // pasada en una descripción que en realidad es de venta.
  if (revende) return "relacionada";
  if (contiene(t, HACER)) return "produccion";
  return "relacionada";
}

/**
 * ¿Esta CSF sirve para registrar una vinícola?
 *
 * @returns {{ veredicto, actividades, produccion, relacionadas, motivo }}
 *   veredicto: 'produccion' | 'solo_relacionadas' | 'sin_vino' | 'sin_actividades'
 *
 * NO devuelve un booleano a propósito. Un sí/no obligaría a esta función a
 * tomar una decisión de negocio que no le toca, y perdería la diferencia entre
 * «su CSF dice que vende vino» y «su CSF no menciona el vino» — que para quien
 * está dando de alta a un cliente son dos conversaciones distintas.
 *
 * Tampoco mira el PORCENTAJE. Decisión del operador: "Pasa aún cuando la
 * actividad no sea la principal." Una vinícola pequeña puede tener el 90% en
 * restaurante y el 10% en elaboración, y sigue siendo una vinícola.
 */
export function evaluarCsfDeVinicola(lineas) {
  const actividades = extraerActividades(lineas)
    .map(a => ({ ...a, clase: clasificarActividad(a.descripcion) }));

  const produccion = actividades.filter(a => a.clase === "produccion");
  const relacionadas = actividades.filter(a => a.clase === "relacionada");

  let veredicto, motivo;
  if (actividades.length === 0) {
    veredicto = "sin_actividades";
    motivo = "No se pudieron leer las actividades económicas de esta CSF. " +
             "Puede ser un formato distinto o un documento incompleto: revísalo a ojo.";
  } else if (produccion.length) {
    veredicto = "produccion";
    motivo = produccion.length === 1
      ? `Su CSF registra «${produccion[0].descripcion}».`
      : `Su CSF registra ${produccion.length} actividades de producción.`;
  } else if (relacionadas.length) {
    veredicto = "solo_relacionadas";
    motivo = `Su CSF habla de vino pero no de producirlo: ` +
             relacionadas.map(a => `«${a.descripcion}»`).join(", ") + ". " +
             "Eso es comercio, no elaboración.";
  } else {
    veredicto = "sin_vino";
    motivo = `Ninguna de sus ${actividades.length} actividades menciona vino, uva ` +
             "ni bebidas fermentadas.";
  }

  return { veredicto, actividades, produccion, relacionadas, motivo };
}
